/**
 * SkillMatrixGrid.tsx
 * Employee × Skill matrix. Features: Excel-style range selection, colored
 * level chips, category-group collapse, collapsible Job Class / Work Group
 * meta-columns, drag-fill, vertical column headers, per-employee coverage strip.
 *
 * Row rendering delegated to SkillMatrixGridRow (memoized).
 * Meta-column header cells rendered by MetaColumnHeaderCell.
 * Collapse persistence via skill-matrix/groupCollapse + metaColumnCollapse.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import { SKILL_LEVEL_LABELS } from '../../../components/schedule/SkillLevelChip';
import { HoverStatusStrip } from './skill-matrix/HoverStatusStrip';
import type { HoverInfo } from './skill-matrix/HoverStatusStrip';
import { SkillMatrixLegend } from './skill-matrix/SkillMatrixLegend';
import type { SkillMatrixCell, MatrixSkill, SkillUser } from '../../../services/scheduleSkillMatrix';
import { useSkillMatrixStore } from '../../../state/skillMatrixStore';
import {
  resolveSelectedCoords,
  buildSelectionSet,
  cellKey,
  computeSelectionEdges,
} from './skill-matrix/selectionMath';
import type { CellCoord } from './skill-matrix/selectionMath';
import type { DragPreviewRect } from './skill-matrix/useDragFill';
import { SkillMatrixGridRow } from './SkillMatrixGridRow';
import type { GroupedSkills } from './SkillMatrixGridRow';
import type { Density } from './skill-matrix/density';
import { readCollapsedGroups, writeCollapsedGroups } from './skill-matrix/groupCollapse';
import {
  readCollapsedMetaColumns,
  writeCollapsedMetaColumns,
  type MetaColumn,
} from './skill-matrix/metaColumnCollapse';
import { MetaColumnHeaderCell } from './skill-matrix/MetaColumnHeaderCell';

/** When the task-type column count exceeds this threshold, headers go vertical. */
const ROTATE_HEADERS_THRESHOLD = 12;

/** Height of the column-header row when headers are in vertical mode (px). */
const ROTATED_HEADER_ROW_HEIGHT = 120;

/** Width of the sticky Coverage column (px). */
const COVERAGE_COL_WIDTH = 120;

/** Width of a collapsed category group strip (px). */
const COLLAPSED_GROUP_WIDTH = 50;

type CellIndex = Map<string, SkillMatrixCell>;

function buildCellIndex(cells: SkillMatrixCell[]): CellIndex {
  const index: CellIndex = new Map();
  for (const cell of cells) {
    index.set(`${cell.UserKey}::${cell.SkillKey}`, cell);
  }
  return index;
}

function groupSkillsByCategory(skills: MatrixSkill[]): GroupedSkills[] {
  const groups: Map<string, MatrixSkill[]> = new Map();
  for (const tt of skills) {
    const cat = tt.Category ?? 'Other';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push(tt);
  }
  return Array.from(groups.entries()).map(([category, items]) => ({ category, items }));
}

interface SkillMatrixGridProps {
  cells: SkillMatrixCell[];
  users: SkillUser[];
  skills: MatrixSkill[];
  search: string;
  density: Density;
  /** When true the grid is view-only — no click/drag/keyboard mutation (BUG-2026-0098). */
  readOnly: boolean;
  /** Graph photo blob URLs keyed by email (may be empty while photos load). */
  photos: Record<string, string | null>;
  onCellClick: (cell: SkillMatrixCell, anchorEl: HTMLElement) => void;
  /** Called on plain click (no shift) to cycle the cell's level. */
  onCellCycle: (coord: CellCoord) => void;
  onCellMouseDown: (coord: CellCoord, e: React.MouseEvent) => void;
  onCellMouseEnter: (coord: CellCoord, e: React.MouseEvent) => void;
  /** Called when the user mouse-downs on the fill handle of the focused cell. */
  onHandleMouseDown: (e: React.MouseEvent) => void;
  /** Cells currently highlighted in the drag-fill preview (null when not dragging). */
  dragPreviewRect: DragPreviewRect | null;
  /** True when the caller may edit users' Tier (Lab Admin — PUT /users/:key). */
  canEditTier: boolean;
  /** Persist a Tier change for a user (null clears back to auto-derive). */
  onTierChange: (appUserKey: string, tier: string | null) => void;
}

export const SkillMatrixGrid: React.FC<SkillMatrixGridProps> = ({
  cells,
  users,
  skills,
  search,
  density,
  readOnly,
  photos,
  onCellClick,
  onCellCycle,
  onCellMouseDown,
  onCellMouseEnter,
  onHandleMouseDown,
  dragPreviewRect,
  canEditTier,
  onTierChange,
}) => {
  const theme = useTheme();

  // Read selection state from store.
  const selection = useSkillMatrixStore((s) => s.selection);

  // ── Hover state — drives the bottom status strip ─────────────────────────────
  const [hoveredCoord, setHoveredCoord] = useState<CellCoord | null>(null);

  // ── Collapsed groups (localStorage-persisted) ────────────────────────────────
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(readCollapsedGroups()),
  );

  const toggleGroup = useCallback((category: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      writeCollapsedGroups(next);
      return next;
    });
  }, []);

  // ── Collapsed meta columns — Job Class / Work Group (localStorage-persisted) ─
  const [collapsedMetaColumns, setCollapsedMetaColumns] = useState<Set<MetaColumn>>(
    () => new Set(readCollapsedMetaColumns()),
  );

  const toggleMetaColumn = useCallback((col: MetaColumn) => {
    setCollapsedMetaColumns((prev) => {
      const next = new Set(prev);
      if (next.has(col)) {
        next.delete(col);
      } else {
        next.add(col);
      }
      writeCollapsedMetaColumns(next);
      return next;
    });
  }, []);

  // Group task types by category for the column group headers
  const groups = useMemo(() => groupSkillsByCategory(skills), [skills]);

  // Visible task types — excludes collapsed groups
  const visibleSkills = useMemo(
    () => skills.filter((tt) => {
      const cat = tt.Category ?? 'Other';
      return !collapsedGroups.has(cat);
    }),
    [skills, collapsedGroups],
  );

  // Derive a Set of visible task type keys (for coverage computation in rows)
  const visibleSkillKeys = useMemo(
    () => new Set(visibleSkills.map((tt) => tt.SkillKey)),
    [visibleSkills],
  );

  // Compute the set of selected composite keys for O(1) lookup.
  const selectedKeys = useMemo(
    () => buildSelectionSet(resolveSelectedCoords(selection.anchor, selection.focus, users, visibleSkills)),
    [selection.anchor, selection.focus, users, visibleSkills],
  );

  // Compute the selection-edge map for the Excel-style perimeter box.
  // Non-empty only when 2+ cells are selected; single-cell focus produces an empty map.
  const selectionEdges = useMemo(
    () => computeSelectionEdges(selection.anchor, selection.focus, users, visibleSkills),
    [selection.anchor, selection.focus, users, visibleSkills],
  );

  // Compute the set of keys that are in the current drag-fill preview.
  const previewKeys = useMemo((): Set<string> => {
    if (!dragPreviewRect) return new Set();
    const { startCoord, endCoord } = dragPreviewRect;
    const startRowIdx = users.findIndex((u) => u.AppUserKey === startCoord.userKey);
    const endRowIdx   = users.findIndex((u) => u.AppUserKey === endCoord.userKey);
    const startColIdx = visibleSkills.findIndex((tt) => tt.SkillKey === startCoord.skillKey);
    const endColIdx   = visibleSkills.findIndex((tt) => tt.SkillKey === endCoord.skillKey);
    if (startRowIdx === -1 || endRowIdx === -1 || startColIdx === -1 || endColIdx === -1) {
      return new Set();
    }
    const keys = new Set<string>();
    for (let r = startRowIdx; r <= endRowIdx; r++) {
      for (let c = startColIdx; c <= endColIdx; c++) {
        keys.add(cellKey(users[r].AppUserKey, visibleSkills[c].SkillKey));
      }
    }
    return keys;
  }, [dragPreviewRect, users, visibleSkills]);

  // Build cell lookup index
  const cellIndex = useMemo(() => buildCellIndex(cells), [cells]);

  // Resolve hover info for the status strip
  const hoverInfo = useMemo((): HoverInfo | null => {
    if (!hoveredCoord) return null;
    const user = users.find((u) => u.AppUserKey === hoveredCoord.userKey);
    const tt = skills.find((t) => t.SkillKey === hoveredCoord.skillKey);
    const cell = cellIndex.get(cellKey(hoveredCoord.userKey, hoveredCoord.skillKey));
    const level = cell?.Level ?? null;
    return {
      user: user?.DisplayName ?? '',
      task: tt?.Name ?? '',
      levelLabel:
        level != null
          ? `Level ${level} — ${SKILL_LEVEL_LABELS[level] ?? ''}`
          : 'No skill recorded',
    };
  }, [hoveredCoord, users, skills, cellIndex]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.DisplayName.toLowerCase().includes(q) ||
        u.Email.toLowerCase().includes(q),
    );
  }, [users, search]);

  // Whether to use vertical column headers (based on visible count only)
  const rotateHeaders = visibleSkills.length > ROTATE_HEADERS_THRESHOLD;

  const stickyHeaderSx = {
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
    bgcolor: theme.table.headerBg,
    fontWeight: 700,
    fontSize: '0.7rem',
    whiteSpace: 'nowrap' as const,
  };

  // Whether any columns are visible (all groups collapsed → empty state)
  const hasVisibleColumns = visibleSkills.length > 0;

  // Total colSpan for empty-state cells (employee + jobClass + workGroup + tier + visible tasks + coverage)
  const totalColSpan = 4 + visibleSkills.length + groups.filter((g) => collapsedGroups.has(g.category)).length + 1;

  return (
    // Outer wrapper does NOT scroll. Only the inner TableContainer scrolls, so
    // the HoverStatusStrip + Legend below stay visible while the user scrolls
    // the matrix horizontally to reach more test-type columns (BUG-2026-0088).
    <Box
      component={Paper}
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 200px)',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <TableContainer
        sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'auto', minWidth: 600 }}>
        <TableHead>
          {/* Category group row */}
          <TableRow>
            {/* Employee / Job Class / Work Group cols */}
            <TableCell sx={{ ...stickyHeaderSx, left: 0, zIndex: 3 }} />
            <TableCell
              sx={{
                ...stickyHeaderSx,
                ...(collapsedMetaColumns.has('jobClass') && {
                  width: COLLAPSED_GROUP_WIDTH,
                  minWidth: COLLAPSED_GROUP_WIDTH,
                  maxWidth: COLLAPSED_GROUP_WIDTH,
                }),
              }}
            />
            <TableCell
              sx={{
                ...stickyHeaderSx,
                ...(collapsedMetaColumns.has('workGroup') && {
                  width: COLLAPSED_GROUP_WIDTH,
                  minWidth: COLLAPSED_GROUP_WIDTH,
                  maxWidth: COLLAPSED_GROUP_WIDTH,
                }),
              }}
            />
            <TableCell
              sx={{
                ...stickyHeaderSx,
                ...(collapsedMetaColumns.has('tier') && {
                  width: COLLAPSED_GROUP_WIDTH,
                  minWidth: COLLAPSED_GROUP_WIDTH,
                  maxWidth: COLLAPSED_GROUP_WIDTH,
                }),
              }}
            />

            {groups.map(({ category, items }) => {
              const isCollapsed = collapsedGroups.has(category);
              if (isCollapsed) {
                // Collapsed: single narrow clickable strip showing the category + chevron
                return (
                  <TableCell
                    key={category}
                    colSpan={1}
                    align="center"
                    onClick={() => toggleGroup(category)}
                    sx={{
                      ...stickyHeaderSx,
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                      color: theme.palette.primary.main,
                      cursor: 'pointer',
                      width: COLLAPSED_GROUP_WIDTH,
                      minWidth: COLLAPSED_GROUP_WIDTH,
                      maxWidth: COLLAPSED_GROUP_WIDTH,
                      p: 0.5,
                      '&:hover': { bgcolor: theme.table.hoverBg },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.25,
                      }}
                    >
                      <ChevronRightIcon sx={{ fontSize: '0.85rem', color: theme.palette.primary.main }} />
                      <Box
                        component="span"
                        sx={{
                          writingMode: 'vertical-rl',
                          textOrientation: 'mixed',
                          transform: 'rotate(180deg)',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          lineHeight: 1.2,
                          maxHeight: 90,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {category}
                      </Box>
                    </Box>
                  </TableCell>
                );
              }

              // Expanded: normal spanning header with collapse chevron
              return (
                <TableCell
                  key={category}
                  colSpan={items.length}
                  align="center"
                  onClick={() => toggleGroup(category)}
                  sx={{
                    ...stickyHeaderSx,
                    borderLeft: '2px solid',
                    borderColor: 'divider',
                    color: theme.palette.primary.main,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: theme.table.hoverBg },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <ExpandMoreIcon sx={{ fontSize: '0.85rem', color: theme.palette.primary.main }} />
                    {category}
                  </Box>
                </TableCell>
              );
            })}

            {/* Coverage column header — sticky right */}
            <TableCell
              align="center"
              sx={{
                ...stickyHeaderSx,
                position: 'sticky',
                right: 0,
                zIndex: 3,
                borderLeft: '2px solid',
                borderColor: 'divider',
                width: COVERAGE_COL_WIDTH,
                minWidth: COVERAGE_COL_WIDTH,
              }}
            >
              Coverage
            </TableCell>
          </TableRow>

          {/* Column header row — vertical text when > ROTATE_HEADERS_THRESHOLD visible columns */}
          <TableRow sx={rotateHeaders ? { height: ROTATED_HEADER_ROW_HEIGHT } : {}}>
            <TableCell sx={{ ...stickyHeaderSx, top: 33, left: 0, zIndex: 3 }}>
              Employee
            </TableCell>

            {/* Job Class — collapsible */}
            <MetaColumnHeaderCell
              label="Job Class"
              collapsed={collapsedMetaColumns.has('jobClass')}
              stickyHeaderSx={stickyHeaderSx}
              onToggle={() => toggleMetaColumn('jobClass')}
            />

            {/* Work Group — collapsible */}
            <MetaColumnHeaderCell
              label="Work Group"
              collapsed={collapsedMetaColumns.has('workGroup')}
              stickyHeaderSx={stickyHeaderSx}
              onToggle={() => toggleMetaColumn('workGroup')}
            />

            {/* Tier — collapsible. Moved here from Admin → Users (RBAC audit):
                Tier is a scheduling attribute (Employee Schedule row grouping),
                so it's edited where the scheduling profile lives. */}
            <MetaColumnHeaderCell
              label="Tier"
              collapsed={collapsedMetaColumns.has('tier')}
              stickyHeaderSx={stickyHeaderSx}
              onToggle={() => toggleMetaColumn('tier')}
            />

            {groups.flatMap(({ category, items }) => {
              if (collapsedGroups.has(category)) {
                // Collapsed group: render a single empty filler cell
                return [
                  <TableCell
                    key={`${category}__collapsed-header`}
                    sx={{
                      ...stickyHeaderSx,
                      top: 33,
                      width: COLLAPSED_GROUP_WIDTH,
                      minWidth: COLLAPSED_GROUP_WIDTH,
                      maxWidth: COLLAPSED_GROUP_WIDTH,
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                    }}
                  />,
                ];
              }

              return items.map((tt) =>
                rotateHeaders ? (
                  // Vertical text mode: writing-mode vertical-rl eliminates horizontal
                  // overflow that caused header text to "scramble" after collapse/expand.
                  // Re-key on rotation flip to force clean DOM remount.
                  <TableCell
                    key={`${tt.SkillKey}-rot`}
                    align="center"
                    sx={{
                      ...stickyHeaderSx,
                      top: 33,
                      height: ROTATED_HEADER_ROW_HEIGHT,
                      verticalAlign: 'bottom',
                      p: 0.5,
                      cursor: 'default',
                      userSelect: 'none',
                      width: 36,
                      minWidth: 36,
                      maxWidth: 36,
                    }}
                  >
                    <Tooltip title={tt.Name} placement="top" arrow>
                      <Box
                        component="span"
                        sx={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)',
                          whiteSpace: 'nowrap',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          display: 'inline-block',
                          maxHeight: ROTATED_HEADER_ROW_HEIGHT - 16,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {tt.Name}
                      </Box>
                    </Tooltip>
                  </TableCell>
                ) : (
                  // Horizontal mode: truncated label with tooltip.
                  // Re-key on rotation flip to force clean DOM remount.
                  <TableCell
                    key={`${tt.SkillKey}-h`}
                    align="center"
                    sx={{
                      ...stickyHeaderSx,
                      top: 33,
                      maxWidth: 80,
                      cursor: 'default',
                      userSelect: 'none',
                    }}
                  >
                    <Tooltip title={tt.Name} placement="top" arrow>
                      <span>{tt.Name.length > 12 ? `${tt.Name.slice(0, 11)}…` : tt.Name}</span>
                    </Tooltip>
                  </TableCell>
                ),
              );
            })}

            {/* Coverage column — second header row filler (sticky right) */}
            <TableCell
              sx={{
                ...stickyHeaderSx,
                top: 33,
                position: 'sticky',
                right: 0,
                zIndex: 3,
                width: COVERAGE_COL_WIDTH,
                minWidth: COVERAGE_COL_WIDTH,
              }}
            />
          </TableRow>
        </TableHead>

        <TableBody>
          {!hasVisibleColumns ? (
            /* All groups collapsed — show expand prompt */
            <TableRow>
              <TableCell
                colSpan={totalColSpan}
                align="center"
                sx={{ py: 4, color: 'text.secondary' }}
              >
                No columns visible — expand a category to see skill data.
              </TableCell>
            </TableRow>
          ) : filteredUsers.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={totalColSpan}
                align="center"
                sx={{ py: 4, color: 'text.secondary' }}
              >
                No employees match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            filteredUsers.map((user, rowIdx) => (
              <SkillMatrixGridRow
                key={user.AppUserKey}
                user={user}
                rowIdx={rowIdx}
                groups={groups}
                collapsedGroups={collapsedGroups}
                collapsedMetaColumns={collapsedMetaColumns}
                cellIndex={cellIndex}
                cells={cells}
                visibleSkillKeys={visibleSkillKeys}
                selectedKeys={selectedKeys}
                selectionEdges={selectionEdges}
                previewKeys={previewKeys}
                focusCoord={selection.focus}
                isDragging={dragPreviewRect !== null}
                density={density}
                readOnly={readOnly}
                photos={photos}
                canEditTier={canEditTier}
                onTierChange={onTierChange}
                onCellClick={onCellClick}
                onCellCycle={onCellCycle}
                onCellMouseDown={onCellMouseDown}
                onCellMouseEnter={onCellMouseEnter}
                onHandleMouseDown={onHandleMouseDown}
                onCellHover={setHoveredCoord}
              />
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Hover status strip + Legend — siblings BELOW the scroll container so they
          stay visible during horizontal scroll (BUG-2026-0088). */}
      <HoverStatusStrip hoverInfo={hoverInfo} />
      <SkillMatrixLegend />
    </Box>
  );
};
